import React, { FunctionComponent, ReactElement } from "react"
import { DfCpd } from "@store/services/converter/types"
import { NormalDistributionChart } from "@components/Distribution/NormalDistributionChart/NormalDistributionChart"
import { UniformDistributionChart } from "@components/Distribution/UniformDistributionChart/UniformDistributionChart"
import { Distribution } from "@custom-types/graphs"
import { BinomialDistributionChart } from "@components/Distribution/BinomialDistributionChart/BinomialDistributionChart"
import { WeibullDistributionChart } from "@components/Distribution/WeibullDistributionChart/WeibullDistributionChart"
import { WaldDistributionChart } from "@components/Distribution/WaldDistributionChart/WaldDistributionChart"
import { VonmisesDistributionChart } from "@components/Distribution/VonmisesDistributionChart/VonmisesDistributionChart"
import { LogisticDistributionChart } from "@components/Distribution/LogisticDistributionChart/LogisticDistributionChart"
import { ExponentialDistributionChart } from "@components/Distribution/ExponentialDistributionChart/ExponentialDistributionChart"
import { Box } from "@mui/material"

interface Props {
  dfCpd: DfCpd
  rowIndex: number
}

const getNumeric = (value: string | null | undefined): number => {
  return parseFloat(value ?? "")
}

const getNumericOptional = (value?: string | null): number | undefined => {
  if (!value) return undefined
  return parseFloat(value)
}

const ChartContainer: FunctionComponent<{ children: ReactElement }> = ({ children }) => {
  return (
    <Box
      data-testid="chart-container"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        maxHeight: 300,
        mt: 2,
      }}
    >
      {children}
    </Box>
  )
}

export const DistributionChart: FunctionComponent<Props> = ({ dfCpd, rowIndex }) => {
  const safeIndex = dfCpd.distribution?.[rowIndex] ? rowIndex : 0
  const type = dfCpd.distribution?.[safeIndex]

  switch (type) {
    case Distribution.Normal: {
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <NormalDistributionChart loc={loc} scale={scale} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
    case Distribution.Uniform: {
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <UniformDistributionChart loc={loc} scale={scale} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
    case Distribution.Weibull: {
      const c = getNumeric(dfCpd.c?.[safeIndex])
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <WeibullDistributionChart c={c} loc={loc} scale={scale} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
    case Distribution.Binomial: {
      const n = getNumeric(dfCpd.n?.[safeIndex])
      const p = getNumeric(dfCpd.p?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <BinomialDistributionChart n={n} p={p} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
    case Distribution.Wald: {
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <WaldDistributionChart loc={loc} scale={scale} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
    case Distribution.Vonmises: {
      const kappa = getNumeric(dfCpd.kappa?.[safeIndex])
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <VonmisesDistributionChart
            kappa={kappa}
            loc={loc}
            scale={scale}
            lbound={lbound}
            ubound={ubound}
          />
        </ChartContainer>
      )
    }
    case Distribution.Logistic: {
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <LogisticDistributionChart loc={loc} scale={scale} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
    case Distribution.Exponential: {
      const loc = getNumeric(dfCpd.loc?.[safeIndex])
      const scale = getNumeric(dfCpd.scale?.[safeIndex])
      const lbound = getNumericOptional(dfCpd.lbound?.[safeIndex])
      const ubound = getNumericOptional(dfCpd.ubound?.[safeIndex])
      return (
        <ChartContainer>
          <ExponentialDistributionChart loc={loc} scale={scale} lbound={lbound} ubound={ubound} />
        </ChartContainer>
      )
    }
  }
  return null
}
